import { useState } from 'react';
import {
  LIBELLE_MOYEN_RELANCE, MOYENS_RELANCE,
  type CibleRelance, type MotifRelance, type MoyenRelance,
} from '@mosaik/core';
import { ErreurApi } from '../api/client';
import { useRelanceHorsMail } from '../api/hooks';
import { useToasts } from './Toasts';

/**
 * « Déjà relancé autrement » — le geste fait hors de l'outil.
 *
 * Posé **à côté du bouton qui ouvre le mail**, dans la même rangée : les deux
 * répondent à la même question — « comment je relance ? » — et c'est devant ce
 * bouton qu'on se souvient d'avoir déjà téléphoné. Le placer dans la fenêtre du
 * mail obligeait à l'ouvrir pour dire qu'on n'en avait pas besoin.
 *
 * Le panneau de saisie prend toute la largeur (`flexBasis: 100%`) et passe donc
 * à la ligne sous les boutons : la rangée reste lisible quand il est ouvert.
 *
 * **Le moyen est obligatoire, et c'est tout l'équilibre de ce bouton.** La règle
 * posée à l'origine — « une relance EST un mail » — existait pour qu'on ne
 * puisse pas inscrire une relance qui n'a pas eu lieu : le mail en était la
 * preuve. Ici il n'y a pas de preuve, seulement la parole de qui enregistre.
 * Exiger de dire COMMENT, et l'écrire à l'historique, remplace la preuve par une
 * affirmation datée et signée.
 */
export function RelanceHorsMail({
  commande, cible, motif, fournisseur, enAttente, onFait,
}: {
  /** Seuls l'identifiant et la référence servent : de quoi appeler et nommer. */
  commande: { id: string; ref: string };
  cible: CibleRelance;
  /**
   * Ce qu'on réclame — décidé par l'appelant, qui a la commande entière sous la
   * main. C'est cela, et non le moyen, que relira la colonne « Litige ».
   */
  motif: MotifRelance;
  /** Société visée quand la commande en compte plusieurs. */
  fournisseur?: string | null;
  enAttente?: boolean;
  /** Appelé une fois la relance enregistrée — la fenêtre hôte se ferme. */
  onFait?: () => void;
}) {
  const { ajouter } = useToasts();
  const relance = useRelanceHorsMail();
  const [ouvert, setOuvert] = useState(false);
  const [moyen, setMoyen] = useState<MoyenRelance>('telephone');
  const [commentaire, setCommentaire] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);

  const enregistrer = async () => {
    setErreur(null);
    try {
      await relance.mutateAsync({
        id: commande.id,
        cible,
        motif,
        moyen,
        ...(fournisseur ? { fournisseur } : {}),
        ...(commentaire.trim() ? { commentaire: commentaire.trim() } : {}),
      });
      ajouter(`${commande.ref} : relance enregistrée (${LIBELLE_MOYEN_RELANCE[moyen]}).`);
      setOuvert(false);
      setCommentaire('');
      onFait?.();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "L'enregistrement a échoué.");
    }
  };

  return (
    <>
      <button
        type="button" className="btn" disabled={enAttente || relance.isPending}
        aria-expanded={ouvert} style={{ marginRight: 'auto' }}
        onClick={() => setOuvert((o) => !o)}
      >
        Déjà relancé autrement
      </button>

      {ouvert && (
        // `flexBasis: 100%` : le panneau passe sous la rangée de boutons plutôt
        // que de la comprimer.
        <div style={{ flexBasis: '100%', marginTop: 8, order: 1, textAlign: 'left' }}>
          <div className="field">
            <label htmlFor="hm-moyen">Comment la relance a-t-elle été faite ?</label>
            <select
              id="hm-moyen" value={moyen}
              onChange={(e) => setMoyen(e.target.value as MoyenRelance)}
            >
              {MOYENS_RELANCE.map((m) => (
                <option key={m} value={m}>{LIBELLE_MOYEN_RELANCE[m]}</option>
              ))}
            </select>
            <input
              value={commentaire} maxLength={280} style={{ marginTop: 6 }}
              placeholder="ce qui a été dit, ou ce qui a été répondu (facultatif)"
              onChange={(e) => setCommentaire(e.target.value)}
            />
            <p className="hint" style={{ margin: '6px 0 0' }}>
              Compte comme une vraie relance — même compteur, même sursis. Le moyen est inscrit à
              l&apos;historique : sans mail pour faire preuve, c&apos;est lui qui l&apos;atteste.
            </p>
            {erreur && <p className="err" role="alert">{erreur}</p>}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button
                type="button" className="btn pri sm" disabled={relance.isPending}
                onClick={() => void enregistrer()}
              >
                {relance.isPending ? <><span className="spin" /> …</> : 'Enregistrer la relance'}
              </button>
              <button type="button" className="btn sm" onClick={() => setOuvert(false)}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
